import React, { useMemo, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import localforage from 'localforage';
import Pusher from 'pusher-js';
import Echo from 'laravel-echo';
import EchoContext from 'context/EchoContext';

const AUTH_ENDPOINT = `${process.env.REACT_APP_GRAPHQL_SERVER_BASE_URL}/broadcasting/auth`;

const createEchoInstance = (accessToken) => {
  // Pusher logging disabled (mantiene la consola limpia en prod).
  Pusher.logToConsole = false;

  return new Echo({
    broadcaster: 'pusher',
    cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER,
    encrypted: true,
    forceTLS: true,
    key: process.env.REACT_APP_PUSHER_APP_KEY,
    // Authorizer propio: lee el access token VIGENTE (localforage `otx_a`, misma fuente que
    // REST/GraphQL) en CADA auth de canal. Antes el token se fijaba ESTÁTICO al crear Echo, así
    // que tras un refresh el header quedaba obsoleto → /broadcasting/auth devolvía 401.
    authorizer: (channel) => ({
      authorize: async (socketId, callback) => {
        try {
          const token = (await localforage.getItem('otx_a')) || accessToken;
          const response = await fetch(AUTH_ENDPOINT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ socket_id: socketId, channel_name: channel.name })
          });

          if (!response.ok) {
            callback(new Error(`Broadcasting auth failed (${response.status})`), null);
            return;
          }

          callback(null, await response.json());
        } catch (error) {
          callback(error, null);
        }
      }
    })
  });
};

function EchoContextProvider({ children }) {
  const accessToken = useSelector((state) => state.auth.accessToken);
  const echoInstanceRef = useRef(null);
  const previousTokenRef = useRef(null);
  const [echoReady, setEchoReady] = useState(false);

  // Crea/reconecta el Echo SOLO cuando hay un accessToken válido (evita `Bearer null` → 401).
  useEffect(() => {
    if (!accessToken) {
      return undefined;
    }

    // Cambió de usuario (token distinto) con instancia existente → reconectar limpio.
    if (echoInstanceRef.current && previousTokenRef.current && previousTokenRef.current !== accessToken) {
      echoInstanceRef.current.disconnect();
      echoInstanceRef.current = null;
      setEchoReady(false);

      const timeoutId = setTimeout(() => {
        echoInstanceRef.current = createEchoInstance(accessToken);
        previousTokenRef.current = accessToken;
        setEchoReady(true);
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    // Creación inicial (primera vez que hay token).
    if (!echoInstanceRef.current) {
      echoInstanceRef.current = createEchoInstance(accessToken);
      previousTokenRef.current = accessToken;
      setEchoReady(true);
    }

    return undefined;
  }, [accessToken]);

  // Solo exponer el echo cuando está realmente listo.
  const echoContextValue = useMemo(
    () => ({ echo: echoReady ? echoInstanceRef.current : null }),
    [echoReady]
  );

  return <EchoContext.Provider value={echoContextValue}>{children}</EchoContext.Provider>;
}

EchoContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default EchoContextProvider;
